import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { getProfileImageUrl } from '@/lib/utils';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Search, MapPin, MessageSquare, Instagram, Linkedin, Twitter, 
  Globe, Diamond, Send, Navigation, ArrowUpDown, Flag
} from 'lucide-react';
import { DMRequestButton } from '@/components/DMRequestDialog';
import { ReportButton } from '@/components/ReportDialog';

const API_URL = window.location.origin;

const MemberDirectoryPage = () => {
  const { user, updateLocation } = useAuth();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('distance'); // 'distance', 'name', 'diamonds'
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const hasLocation = !!user?.location;

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    let filtered = members;
    
    // Apply search filter
    if (search) {
      filtered = members.filter(m => 
        m.full_name.toLowerCase().includes(search.toLowerCase()) ||
        m.city?.toLowerCase().includes(search.toLowerCase()) ||
        m.state?.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'distance':
          // Put members with distance first, then by distance value
          if (a.distance_miles && b.distance_miles) return a.distance_miles - b.distance_miles;
          if (a.distance_miles) return -1;
          if (b.distance_miles) return 1;
          return a.full_name.localeCompare(b.full_name);
        case 'name':
          return a.full_name.localeCompare(b.full_name);
        case 'diamonds':
          return (b.diamond_points || 0) - (a.diamond_points || 0);
        default:
          return 0;
      }
    });
    
    setFilteredMembers(sorted);
  }, [search, members, sortBy]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/directory`);
      setMembers(response.data);
      setFilteredMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      toast.error('Failed to load member directory');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await updateLocation(position.coords.latitude, position.coords.longitude);
          toast.success('Location enabled! Refreshing directory...');
          // Refetch members to get updated distances
          fetchMembers();
        } catch (error) {
          toast.error('Failed to save location');
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error('Location permission denied. Please enable it in your browser settings.');
        } else {
          toast.error('Could not get your location');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedMember) return;

    setSending(true);
    try {
      await axios.post(`${API_URL}/api/messages`, {
        receiver_id: selectedMember.id,
        content: message
      });
      toast.success('Message sent successfully');
      setShowMessageDialog(false);
      setMessage('');
      setSelectedMember(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getMemberTypeBadgeColor = (type) => {
    switch (type) {
      case 'SDC VIP': return 'bg-purple-500 text-white';
      case 'Coaching Member': return 'bg-terracotta text-white';
      case 'Inner Edge': return 'bg-forest text-white';
      case 'Inner Circle': return 'bg-sand text-forest';
      default: return 'bg-gray-200 text-gray-700';
    }
  };

  const formatDistance = (miles) => {
    if (!miles) return null;
    if (miles < 1) return 'Less than 1 mile away';
    if (miles < 50) return `${Math.round(miles)} miles away`;
    return `${Math.round(miles)} miles away`;
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="member-directory-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest">
            Member Directory
          </h1>
          <p className="text-gray-600 mt-2">
            Connect with fellow Sister Diamonds members in your community.
          </p>
        </div>

        {/* Location Banner */}
        {!hasLocation && (
          <Card className="card-base mb-6 bg-gradient-to-r from-terracotta/10 to-forest/10 border-terracotta/30">
            <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-terracotta/20 flex items-center justify-center">
                  <Navigation className="h-5 w-5 text-terracotta" />
                </div>
                <div>
                  <p className="font-medium text-forest">Enable Location</p>
                  <p className="text-sm text-gray-600">See how close other members are to you</p>
                </div>
              </div>
              <Button 
                onClick={handleEnableLocation} 
                disabled={gettingLocation}
                className="btn-primary"
              >
                <MapPin className="h-4 w-4 mr-2" />
                {gettingLocation ? 'Getting Location...' : 'Share My Location'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Search and Sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by name, city, or state..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-base"
              data-testid="directory-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-gray-500" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="distance">Nearest First</SelectItem>
                <SelectItem value="name">Name (A-Z)</SelectItem>
                <SelectItem value="diamonds">Most Diamonds</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Members Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="card-base animate-pulse">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="h-16 w-16 rounded-full bg-sand" />
                    <div className="flex-1">
                      <div className="h-5 bg-sand rounded w-3/4 mb-2" />
                      <div className="h-4 bg-sand rounded w-1/2" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No members found matching your search.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMembers.map((member) => (
              <Card key={member.id} className="card-base hover:shadow-lg transition-shadow" data-testid={`member-card-${member.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-16 w-16 border-2 border-sand">
                      <AvatarImage src={getProfileImageUrl(member.profile_image)} />
                      <AvatarFallback className="bg-forest text-white text-xl">
                        {member.full_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg font-semibold text-forest truncate">
                        {member.full_name}
                      </h3>
                      <Badge className={`text-xs ${getMemberTypeBadgeColor(member.member_type)}`}>
                        {member.member_type}
                      </Badge>
                      {member.city && member.state && (
                        <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {member.city}, {member.state}
                        </p>
                      )}
                      {member.distance_miles != null && (
                        <p className="text-xs text-terracotta mt-1 flex items-center gap-1">
                          <Navigation className="h-3 w-3" />
                          {formatDistance(member.distance_miles)}
                        </p>
                      )}
                    </div>
                  </div>

                  {member.bio && (
                    <p className="text-sm text-gray-600 mt-4 line-clamp-2">{member.bio}</p>
                  )}

                  {/* Diamond Points */}
                  {member.diamond_points > 0 && (
                    <div className="flex items-center gap-1 mt-3 text-sm text-purple-600">
                      <Diamond className="h-4 w-4" />
                      <span>{member.diamond_points} diamonds</span>
                    </div>
                  )}

                  {/* Social Links */}
                  {member.social_links && Object.values(member.social_links).some(v => v) && (
                    <div className="flex gap-2 mt-4">
                      {member.social_links.instagram && (
                        <a href={member.social_links.instagram} target="_blank" rel="noopener noreferrer"
                           className="text-gray-400 hover:text-pink-500 transition-colors">
                          <Instagram className="h-5 w-5" />
                        </a>
                      )}
                      {member.social_links.linkedin && (
                        <a href={member.social_links.linkedin} target="_blank" rel="noopener noreferrer"
                           className="text-gray-400 hover:text-blue-600 transition-colors">
                          <Linkedin className="h-5 w-5" />
                        </a>
                      )}
                      {member.social_links.twitter && (
                        <a href={member.social_links.twitter} target="_blank" rel="noopener noreferrer"
                           className="text-gray-400 hover:text-blue-400 transition-colors">
                          <Twitter className="h-5 w-5" />
                        </a>
                      )}
                      {member.social_links.website && (
                        <a href={member.social_links.website} target="_blank" rel="noopener noreferrer"
                           className="text-gray-400 hover:text-forest transition-colors">
                          <Globe className="h-5 w-5" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-4">
                    <DMRequestButton
                      recipientId={member.id}
                      recipientName={member.full_name}
                      recipientAvatar={member.profile_image}
                      className="flex-1"
                    />
                    <ReportButton 
                      contentType="user"
                      reportedUserId={member.id}
                      reportedUserName={member.full_name}
                      variant="outline"
                      size="default"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Message Dialog */}
        <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">
                Message {selectedMember?.full_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                className="w-full h-32 p-3 border rounded-lg input-base resize-none"
                data-testid="message-input"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowMessageDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="btn-primary" 
                  onClick={handleSendMessage}
                  disabled={sending || !message.trim()}
                >
                  {sending ? 'Sending...' : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MemberDirectoryPage;
